/**
 * Treatment Record Quick Toolbar host (PRD.182).
 *
 * Glue component that pairs the shared `TreatmentRecordQuickToolbar` with
 * its drawer-backed actions. Drop this into any patient surface that should
 * expose the quick toolbar (Patient Timeline, Treatment Record).
 *
 * Reads the configured Treatment Record drawer-width preset from the
 * Patient Workspace settings so admins can choose Standard / Wide /
 * Expanded centrally.
 */
import { useState } from 'react';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  type PatientWorkspaceSettingsRow,
} from '@helios/data-access';
import { TreatmentRecordQuickToolbar } from './TreatmentRecordQuickToolbar';
import { TreatmentChartingFilesDrawer } from './TreatmentChartingFilesDrawer';
import { TreatmentInvoiceDrawer } from './TreatmentInvoiceDrawer';

interface Props {
  patientId: string;
}

export function TreatmentRecordQuickToolbarHost({ patientId }: Props) {
  const { data: workspaceRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const drawerWidth =
    workspaceRows.length > 0
      ? mapPatientWorkspaceSettingsToUI(workspaceRows[0]).treatmentRecordDrawerWidth
      : 'standard';

  const [charting, setCharting] = useState<{ encounterId: string; date: string } | null>(null);
  const [invoice, setInvoice] = useState<{ encounterId: string; date: string } | null>(null);

  return (
    <>
      <TreatmentRecordQuickToolbar
        patientId={patientId}
        onOpenChartingFiles={(encounterId, date) => setCharting({ encounterId, date })}
        onOpenInvoice={(encounterId, date) => setInvoice({ encounterId, date })}
      />

      <TreatmentChartingFilesDrawer
        open={charting != null}
        onOpenChange={(o) => {
          if (!o) setCharting(null);
        }}
        patientId={patientId}
        initialEncounterId={charting?.encounterId ?? null}
        initialDate={charting?.date ?? null}
        drawerWidth={drawerWidth}
      />

      <TreatmentInvoiceDrawer
        open={invoice != null}
        onOpenChange={(o) => {
          if (!o) setInvoice(null);
        }}
        patientId={patientId}
        initialEncounterId={invoice?.encounterId ?? null}
        initialDate={invoice?.date ?? null}
        drawerWidth={drawerWidth}
      />
    </>
  );
}
